import type { ReactNode } from "react";
import { useId, useState } from "react";
import { ChevronDown } from "lucide-react";

type AccordionProps = {
  title: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  className?: string;
};

export default function Accordion({
  title,
  children,
  defaultOpen = false,
  headingLevel = 4,
  className = "",
}: AccordionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const id = useId();
  const buttonId = `${id}-button`;
  const panelId = `${id}-panel`;
  const heading = renderHeading(headingLevel, buttonId, panelId, isOpen, title, () =>
    setIsOpen((current) => !current),
  );

  return (
    <section
      className={[
        "overflow-hidden rounded-lg border border-gray-200 bg-white",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {heading}

      {isOpen && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={buttonId}
          className="border-t border-gray-100 p-3"
        >
          {children}
        </div>
      )}
    </section>
  );
}

function renderHeading(
  level: NonNullable<AccordionProps["headingLevel"]>,
  buttonId: string,
  panelId: string,
  isOpen: boolean,
  title: ReactNode,
  onToggle: () => void,
) {
  const button = (
    <button
      id={buttonId}
      type="button"
      aria-expanded={isOpen}
      aria-controls={panelId}
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-semibold text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
    >
      <span>{title}</span>
      <ChevronDown
        size={16}
        className={`shrink-0 text-gray-400 transition-transform ${
          isOpen ? "rotate-180" : ""
        }`}
      />
    </button>
  );

  if (level === 2) return <h2>{button}</h2>;
  if (level === 3) return <h3>{button}</h3>;
  if (level === 5) return <h5>{button}</h5>;
  if (level === 6) return <h6>{button}</h6>;
  return <h4>{button}</h4>;
}
